"use client";

import { useEffect, useRef } from "react";
import { openSearchInApp } from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { trackComponent } from "@repo/analytics/analytics";

const APP_HEADER_BACK_CTA = "back_button";
const APP_HEADER_BACK_ENTRY = "device_back";

type AppHeaderProps = {
  placeholder?: string;
};

const AppHeader = ({ placeholder }: AppHeaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Animated placeholder — typing effect matching 3in1cms animatePlaceholder
  useEffect(() => {
    const input = inputRef.current;
    if (!input || !placeholder) return;
    const originalPlaceholder = input.getAttribute("placeholder") ?? "";
    const text = placeholder;
    let i = 0;
    let timer: ReturnType<typeof setTimeout>;
    const type = () => {
      if (i < text.length) {
        input.setAttribute("placeholder", text.substring(0, i + 1));
        i++;
      } else {
        i = 0;
        input.setAttribute("placeholder", "");
      }
      timer = setTimeout(type, 175);
    };
    input.setAttribute("placeholder", "");
    type();
    return () => {
      clearTimeout(timer);
      input.setAttribute("placeholder", originalPlaceholder || text);
    };
  }, [placeholder]);

  // Back button — exact 3in1cms logic with analytics tracking
  const handleBack = async () => {
    if (typeof window === "undefined") return;

    // Analytics tracking — matches 3in1cms AppHeader pattern
    await trackComponent({
      componentName: "AppHeader",
      ctaText: APP_HEADER_BACK_CTA,
      entryPoint: APP_HEADER_BACK_ENTRY,
    });
    window.history.go(-1);
  };

  // Matches web-june-26-dev Search.tsx: native redirect on input focus when in app
  const handleInputFocus = () => {
    if (isOpenInApp()) {
      openSearchInApp("BAR");
    }
  };

  const handleSearchPrompt = (type: "text" | "voice") => {
    openSearchInApp(type === "text" ? "BAR" : "MIC");
  };

  return (
    <div
      role="none"
      className="bg-blue-header bg-[#002142] fixed top-0 left-0 z-10 flex w-full flex-row items-center justify-between py-2 pr-3"
      data-testid="app-pdp-header"
    >
      {/* Back button */}
      <button
        type="button"
        className="bf-icon-arrow rotate-180 cursor-pointer border-none bg-transparent p-3 text-2xl text-white"
        aria-label="Go back"
        data-testid="app-pdp-header-back"
        onClick={(e) => {
          e.stopPropagation();
          handleBack();
        }}
      />

      {/* Search bar */}
      <div className="flex flex-1 flex-row items-center justify-between rounded-l-[3px] bg-white">
        <input
          ref={inputRef}
          className="w-full border-0 bg-transparent py-2 pl-3 text-xs text-gray-900 outline-none"
          type="text"
          placeholder={placeholder}
          aria-label="Search"
          onFocus={handleInputFocus}
        />
        <button
          type="button"
          className="bf-icon-mic px-4 py-[9px] text-2xl text-gray-500"
          aria-label="Voice search"
          onClick={(e) => {
            e.stopPropagation();
            handleSearchPrompt("voice");
          }}
        />
      </div>

      {/* Search icon button */}
      <button
        type="button"
        className="flex h-[42px] items-center justify-center rounded-r-[3px] bg-orange-100 px-3"
        aria-label="Search icon"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSearchPrompt("text");
        }}
      >
        <i className="bf-icon-search text-link-primary text-2xl" />
      </button>
    </div>
  );
};

export default AppHeader;
